/**
 * View What You Have — Aug 2026 (Randy pt-view-what-you-have).
 *
 * Opens from the Earnings tab success card ("You're a Creator Pro · Active")
 * for creators who own the full toolkit. Shows every SKU in an owned state,
 * announces the total storefront value and the amount saved, and — when the
 * platform toggle is on AND the user has an eligible referrer AND hasn't
 * already paid it forward — offers a Pay-It-Forward button that sends the
 * savings amount to the referrer as an optional one-time fist-bump (33% cut
 * to the referrer via Stripe transfer_data; the rest to the platform).
 *
 * Never called a tip. Never appears as an ambush upsell. Always dismissible
 * via a small "No thanks" link that persists across sessions; still reachable
 * via a discreet "› Fist bump your referrer" toggle when dismissed.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Check, Crown, HandHeart, ArrowRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt$ = (cents) => `$${(((cents || 0)) / 100).toFixed(0)}`;

function flattenCatalog(cat) {
  const rows = [];
  for (const s of cat?.suites || []) {
    for (const it of s.items || []) {
      // Oct 1, 2026 — an unbuilt SKU is never "owned". This modal green-ticks
      // every row it renders, so a registered-but-unshipped SKU (the first
      // being Silo Anywhere) was being advertised to full-bundle owners as a
      // tool they already have. Skip it here; it appears the day the code
      // ships and `needs_code` flips to false.
      if (it.needs_code) continue;
      rows.push({
        suite: s.label || s.key,
        key: it.key,
        name: it.name || it.label || it.key,
        description: it.description || "",
        day1_cents: it.day1_cents || 0,
        locked: !!it.locked,
        required: !!it.required,
        base_workbench: !!it.base_workbench,
        depth: 0,
      });
      for (const ch of it.children || []) {
        if (ch.needs_code) continue;
        rows.push({
          suite: s.label || s.key,
          key: ch.key,
          name: ch.name || ch.label || ch.key,
          description: ch.description || "",
          day1_cents: ch.day1_cents || 0,
          locked: !!ch.locked,
          required: !!ch.required,
          base_workbench: !!ch.base_workbench,
          depth: 1,
        });
      }
    }
  }
  return rows;
}

export default function ViewWhatYouHaveModal({ open, onOpenChange }) {
  const [catalog, setCatalog] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  // Iter160x (Randy pt-view-what-you-have-descriptions, Feb 2026) — the
  // SKU list rows are individually expandable. Each row is a button; tap
  // toggles that SKU's description panel below it. Multiple rows can be
  // open at once (independent — Randy: "each having its own literal
  // drop-down"). Rows with no description in the catalog stay static
  // (not clickable, no chevron).
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [c, s] = await Promise.all([
          api.get("/creator-pro/catalog"),
          api.get("/me/pay-forward/status"),
        ]);
        if (!alive) return;
        setCatalog(c.data?.catalog || c.data);
        setStatus(s.data);
      } catch {
        toast.error("Could not load your Creator Pro details.", { id: "view-what-you-have-load" });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const rows = useMemo(() => (catalog ? flattenCatalog(catalog) : []), [catalog]);
  const bundleCents = catalog?.bundle?.day1_cents || 0;
  const storefrontValueCents = status?.storefront_value_cents || 0;
  const savingsCents = status?.savings_cents || 0;

  const showPayForward =
    !!status?.globally_enabled &&
    !!status?.eligible &&
    !!status?.referrer_name &&
    !status?.paid_at;

  const setDismissed = async (dismissed) => {
    try {
      await api.post("/me/pay-forward/dismiss", { dismissed });
      setStatus((s) => ({ ...(s || {}), dismissed }));
    } catch {
      toast.error("Could not save that preference.");
    }
  };

  const startPayForward = async () => {
    setPaying(true);
    try {
      const r = await api.post("/me/pay-forward/checkout", { origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
      else throw new Error("no url");
    } catch (e) {
      setPaying(false);
      toast.error(e.response?.data?.detail || "Could not start Stripe checkout.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#1C1B1A] border border-white/10 text-[#FDFCF8] p-6 sm:p-8 siloart-encased overflow-y-auto max-h-[92vh]"
        data-testid="view-what-you-have-modal"
      >
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C16E5A] mb-1 flex items-center gap-2">
            <Crown size={12} weight="duotone" /> Your Creator Pro toolkit
          </div>
          <DialogTitle className="font-serif-display text-2xl sm:text-3xl text-[#FDFCF8]" data-testid="view-what-you-have-title">
            You own the whole toolkit
          </DialogTitle>
          <DialogDescription className="text-sm text-[#E5E5E0] mt-1">
            {loading
              ? "Loading your details…"
              : (
                <>
                  Full storefront value:{" "}
                  <span className="text-[#FDFCF8] font-medium tabular-nums" data-testid="view-what-you-have-value">
                    {fmt$(storefrontValueCents)}
                  </span>{" "}
                  · You paid{" "}
                  <span className="text-[#FDFCF8] font-medium tabular-nums">{fmt$(bundleCents)}</span>{" "}
                  · That&apos;s{" "}
                  <span className="text-[#CDE0CF] font-medium tabular-nums" data-testid="view-what-you-have-savings">
                    {fmt$(savingsCents)}
                  </span>{" "}
                  saved. Nice one.
                </>
              )
            }
          </DialogDescription>
        </DialogHeader>

        {/* SKU ownership list — Iter160x (Randy): each row is now a
            tappable disclosure. Tap opens the description below, tap
            again to collapse. Rows with no description stay static
            (no chevron, no click affordance). */}
        {!loading && catalog && (
          <div className="mt-5 border-t border-white/10 pt-4" data-testid="view-what-you-have-skus">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A8A8A0] mb-2">Everything unlocked</div>
            <ul className="divide-y divide-white/5">
              {rows.map((r) => {
                const hasDesc = Boolean(r.description && r.description.trim());
                const isOpen = expandedKeys.has(r.key);
                return (
                  <li
                    key={r.key}
                    className={r.depth > 0 ? "pl-6" : ""}
                    data-testid={`view-what-you-have-sku-${r.key}`}
                  >
                    {hasDesc ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(r.key)}
                        aria-expanded={isOpen}
                        aria-controls={`view-what-you-have-desc-${r.key}`}
                        data-testid={`view-what-you-have-sku-row-${r.key}`}
                        className="w-full flex items-center justify-between py-2 gap-3 text-left hover:bg-white/[0.03] rounded-sm px-1 -mx-1 transition-colors"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-5 h-5 rounded-full bg-[#4A5D4E] text-white flex items-center justify-center shrink-0">
                            <Check size={11} weight="bold" />
                          </span>
                          <span className="text-sm truncate">{r.name}</span>
                          <CaretDown
                            size={11}
                            weight="bold"
                            className={`text-[#A8A8A0] shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                            data-testid={`view-what-you-have-sku-chevron-${r.key}`}
                          />
                        </div>
                        <span className="text-xs text-[#A8A8A0] tabular-nums pl-3 shrink-0">
                          {fmt$(r.day1_cents)}
                        </span>
                      </button>
                    ) : (
                      <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-5 h-5 rounded-full bg-[#4A5D4E] text-white flex items-center justify-center shrink-0">
                            <Check size={11} weight="bold" />
                          </span>
                          <span className="text-sm truncate">{r.name}</span>
                        </div>
                        <span className="text-xs text-[#A8A8A0] tabular-nums pl-3 shrink-0">
                          {fmt$(r.day1_cents)}
                        </span>
                      </div>
                    )}
                    {hasDesc && isOpen && (
                      <div
                        id={`view-what-you-have-desc-${r.key}`}
                        data-testid={`view-what-you-have-sku-desc-${r.key}`}
                        className="pb-3 pl-8 pr-2 text-[12.5px] leading-relaxed text-[#C7C7C0]"
                      >
                        {r.description}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Pay-It-Forward — only when globally on + eligible + has referrer */}
        {!loading && showPayForward && !status?.dismissed && (
          <div
            className="mt-5 border-t border-white/10 pt-4 bg-white/[0.03] rounded-md p-4"
            data-testid="view-what-you-have-pay-forward"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C16E5A] mb-2">
              <HandHeart size={14} weight="duotone" /> Pay it forward
            </div>
            <p className="text-sm text-[#E5E5E0] leading-relaxed">
              <span className="text-[#FDFCF8] font-medium">{status.referrer_name}</span> introduced you to
              My Silo. If you feel like it, you can send{" "}
              <span className="text-[#FDFCF8] font-medium tabular-nums">{fmt$(savingsCents)}</span> their
              way as a fist-bump — <span className="text-[#CDE0CF]">they keep 33% (
              <span className="tabular-nums">{fmt$(Math.round(savingsCents * 0.33))}</span>)</span> as a
              partner bonus and it never touches your monthly subscription. Fully optional, anytime.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={startPayForward}
                disabled={paying}
                data-testid="view-what-you-have-pay-forward-button"
                className="bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-2.5 rounded-md flex items-center gap-2 text-sm font-medium disabled:opacity-60 transition-colors"
              >
                {paying ? "Opening Stripe…" : (
                  <>
                    Fist-bump {status.referrer_name} · {fmt$(savingsCents)} <ArrowRight size={14} />
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                data-testid="view-what-you-have-dismiss"
                className="text-xs text-[#A8A8A0] hover:text-[#FDFCF8] underline-offset-2 hover:underline"
              >
                No thanks
              </button>
            </div>
          </div>
        )}

        {/* Discreet toggle back — appears only when the user has dismissed */}
        {!loading && showPayForward && status?.dismissed && (
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={() => setDismissed(false)}
              data-testid="view-what-you-have-reveal"
              className="text-xs text-[#A8A8A0] hover:text-[#C16E5A] underline-offset-2 hover:underline"
            >
              › Fist bump your referrer
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
